import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Per-user memory store for Kate.
 *
 * Memories are short, durable facts the user has explicitly told Kate to
 * remember (preferences, definitions, entity relationships, default filters).
 * They're injected into every system-prompt turn, so Kate carries context
 * across sessions without the user having to repeat themselves.
 *
 * Design notes:
 *
 *   - Storage is `localStorage` per the user's browser. We deliberately did
 *     NOT add a backend table — memories are personal preferences and
 *     different execs will want different things remembered. If we ever
 *     decide to share memories across devices we can swap this out for an
 *     /api/ai/memories endpoint without changing the consumer surface.
 *
 *   - Soft cap at MAX_MEMORIES (30). When exceeded, the oldest memory by
 *     createdAt is dropped silently. This keeps the system-prompt token
 *     budget bounded regardless of user behavior.
 *
 *   - No embeddings / RAG. With <30 small memories the cheaper move is to
 *     include them all every turn. Easy to upgrade later if the list grows.
 */

const MAX_MEMORIES = 30;
const MAX_TITLE_LENGTH = 120;
const MAX_CONTENT_LENGTH = 1000;

export interface Memory {
  id: string;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
}

interface MemoriesState {
  memories: Memory[];

  add: (m: { title: string; content: string }) => Memory | { error: string };
  update: (id: string, patch: { title?: string; content?: string }) => boolean;
  remove: (id: string) => boolean;
  clearAll: () => void;
}

const newId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as unknown as { randomUUID: () => string }).randomUUID()
    : `mem_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const sanitize = (s: string, max: number): string => s.trim().slice(0, max);

export const useMemoryStore = create<MemoriesState>()(
  persist(
    (set, get) => ({
      memories: [],

      add: ({ title, content }) => {
        const t = sanitize(title || "", MAX_TITLE_LENGTH);
        const c = sanitize(content || "", MAX_CONTENT_LENGTH);
        if (!t) return { error: "title is required" };
        if (!c) return { error: "content is required" };

        const now = Date.now();
        const memory: Memory = {
          id: newId(),
          title: t,
          content: c,
          createdAt: now,
          updatedAt: now,
        };

        set((s) => {
          const next = [memory, ...s.memories];
          if (next.length > MAX_MEMORIES) {
            // Drop oldest by createdAt to bound system-prompt cost.
            next.sort((a, b) => b.createdAt - a.createdAt);
            next.length = MAX_MEMORIES;
          }
          return { memories: next };
        });
        return memory;
      },

      update: (id, patch) => {
        const existing = get().memories.find((m) => m.id === id);
        if (!existing) return false;
        const t =
          typeof patch.title === "string"
            ? sanitize(patch.title, MAX_TITLE_LENGTH)
            : existing.title;
        const c =
          typeof patch.content === "string"
            ? sanitize(patch.content, MAX_CONTENT_LENGTH)
            : existing.content;
        if (!t || !c) return false;

        set((s) => ({
          memories: s.memories.map((m) =>
            m.id === id ? { ...m, title: t, content: c, updatedAt: Date.now() } : m,
          ),
        }));
        return true;
      },

      remove: (id) => {
        const before = get().memories.length;
        set((s) => ({ memories: s.memories.filter((m) => m.id !== id) }));
        return get().memories.length < before;
      },

      clearAll: () => set({ memories: [] }),
    }),
    {
      name: "telemetry-kate-memories",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const MEMORY_LIMITS = {
  MAX_MEMORIES,
  MAX_TITLE_LENGTH,
  MAX_CONTENT_LENGTH,
};
